import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Page from 'components/Page';
import CTTabs from 'components/CTTabs';
import * as DriverAction from 'actions/driver-action';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import DriverForm from './driver-form';
import PageHeader from 'global/PageHeader';
import {
  Box, Button, ButtonGroup, Chip, CircularProgress,
  Grid,
  IconButton, Link, Typography,
  useTheme
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ToastHandler from 'components/Toast-Handler/toast-handler';
import Searchbar from 'global/Searchbar';
import Notificationbar from 'global/Notificationbar';
import FormDrawer from 'components/FormDrawer';
import FormElements from 'components/FormElements';
import ButtonCancel from 'components/ButtonCancel';
import ButtonSave from 'components/ButtonSave';
import FTIcons from 'assets/icons/FTIcons';
import { tokens } from 'theme/tokens';
import { getDriverDocuments } from 'services/driver-service';
import ContentDrawer from 'components/ContentDrawer';
import LabelValueBox from 'components/LabelValueBox';
import ChartContainer from 'components/Charts/chart-container';
import BarChart from 'components/Charts/barchart';
import { getLastFivePayments } from 'services/payment-service';
import DQFileManager from 'containers/Driver/DQManager';
import PieChart from 'components/Charts/piechart';
import { CHART_COLORS } from 'utils/constant';

const TABS = ['AVAILABLE', 'UNAVAILABLE'];
const DRIVER_DETAILS_TABS = ['Information', 'DQ File'];

const DriverPage = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { drivers, searched, toast, isProcessing, save, update, search, changeStatus, resetState } = props;
  const formState = {
    driver: {
      driverType: '',
      fullName: '',
      phone: '',
      email: '',
      baseRate: '',
      licenseNumber: '',
      licenseState: '',
      licenseExpiryDate: '',
      insuranceCarrier: '',
      insuredName: '',
      policyNumber: '',
      policyExpiryDate: '',
      certificateHolder: '',
      city: '',
      state: '',
      country: '',
      isAvailable: true,
    },
  };

  const [driver, setDriver] = useState(formState.driver);
  const [action, setAction] = useState('Save');
  const [tabIndex, setTabIndex] = useState(0);
  const [detailTabIndex, setSelectedDetailTabIndex] = useState(0);
  const [isViewDriverDrawerOpen, setIsViewDriverDrawerOpen] = useState(false);
  const [isDriverFormDrawerOpen, setIsDriverFormDrawerOpen] = useState(false);
  const [isDocumentManagerDrawerOpen, setDocumentManagerDrawerOpen] = useState(false);
  const [isFileImportDialogOpen, setIsFileImportDialogOpen] = useState(false);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [searchParameters, setSearchParameters] = useState({});
  const [submitForm, setSubmitForm] = useState(() => () => { });

  const [selectedDriver, setSelectedDriver] = useState(null);
  const [dqFiles, setDQFiles] = useState([]);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [lastFivePayments, setLastFivePayments] = useState([]);
  const [isAPIProcessing, setAPIProcessing] = useState(false);
  const [dqStatusPieChartData, setDQStatusPieChart] = useState([]);

  useEffect(() => {
    if (!selectedDriver) return;
    setAPIProcessing(true);
    const param = `${selectedDriver.fullName}:${selectedDriver.phone}`;
    Promise.all([
      getLastFivePayments(param),
      getDriverDocuments(selectedDriver.id)
    ])
      .then(([paymentResult, documentsResult]) => {
        // Handle Payments
        const payments = paymentResult && paymentResult.data ? paymentResult.data.data : [];
        const barChartData = payments.map(item => ({
          'PaymentDate': item.paymentDate,
          'Amount': Number(item.amountPaid)
        }));
        setLastFivePayments(barChartData);
        //DQ Files
        if (documentsResult.status === 200 && documentsResult.data) {
          setDQFiles(documentsResult.data);
          getDQStatusPieChart(documentsResult.data);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setAPIProcessing(false);        
        setIsViewDriverDrawerOpen(true);
        setSelectedDetailTabIndex(detailTabIndex);
      });
  }, [detailTabIndex, selectedDriver]);

  const executeSearch = useCallback((searchParams) => {
    if (searchParams && Object.entries(searchParams).length > 0) {
      const value = searchParams['name'];
      setIsSearchEnabled(true);
      setSearchParameters(searchParams);
      search(value);
    } else {
      setIsSearchEnabled(false);
      setSearchParameters({});
    }
  }, [search]);

  const createSearchChip = useCallback(() => {
    return Object.entries(searchParameters).map(([k, v]) => (
      <Chip
        key={k}
        label={v}
        onDelete={(evt) => deleteSearchChip(evt, k)}
        color='secondary'
        size='small'
      />
    ));
  }, [searchParameters]);

  const deleteSearchChip = useCallback((evt, key) => {
    const updatedParams = { ...searchParameters };
    delete updatedParams[key];
    executeSearch(updatedParams);
  }, [searchParameters, executeSearch]);

  const saveFormHandler = useCallback((driverData) => {
    save(driverData);
  }, [save]);

  const updateFormHandler = useCallback((driverData) => {
    update(driverData);
  }, [update]);

  const toggleFormDialog = useCallback(() => {
    setIsDriverFormDrawerOpen(prev => !prev);
  }, []);

  const closeDialog = useCallback(() => {
    setDriver(formState.driver);
    setIsDriverFormDrawerOpen(false);
    setIsFileImportDialogOpen(false);
    setAction('Save');
    resetState('RESET_TOAST_STATE');
  }, [resetState]);

  const editRow = useCallback((row) => {
    setIsDriverFormDrawerOpen(true);
    setAction('Update');
    setDriver({ ...row, ...row.address });
  }, []);

  const changeTab = useCallback((index) => {
    setTabIndex(index);
  }, []);

  const downloadTemplate = useCallback(() => {
    const filename = 'driver_template.csv';
    const headers = ['FullName', 'Phone', 'License', 'LicenseExpiryDate', 'Insurance', 'City', 'State', 'Country'];
    let csvContent = 'data:text/csv;charset=utf-8,' + headers;
    const data = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  }, []);

  const downloadDrivers = useCallback(() => {
    const filename = 'drivers.csv';
    const { drivers } = this.props;

    const csvData = [];
    const headers = ['FullName', 'Phone', 'License', 'LicenseExpiryDate', 'License State', 'Insurance', 'Insurance Provider', 'Policy Number', 'Expiry Date', 'City', 'State', 'Country'];
    csvData.push(headers);

    drivers.forEach(item => {
      let arr = [item.fullName, item.phone, item.licenseNumber, item.licenseExpiryDate, item.licenseState, item.insuredName, item.insuranceCarrier, item.policyNumber, item.policyExpiryDate, item.city, item.state, item.country];
      csvData.push(arr);
    });
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvData.forEach(function (rowArray) {
      let row = rowArray.join(',');
      csvContent += row + '\r\n';
    });
    const data = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', data);
    link.setAttribute('download', filename);
    link.click();
  }, [drivers]);

  const getDQStatusPieChart = (documents) => {
      /* DQ Files */
    const activeDQFiles = documents.filter(doc => new Date() < new Date(doc.expirationDate));
    const expiredDQFiles = documents.filter(doc => new Date() > new Date(doc.expirationDate));
    const expiringWithin1Week = documents.filter(doc => {
      const diffMs = new Date() - new Date(doc.expirationDate);
      const diffInDays = diffMs ? diffMs / (1000 * 60 * 60 * 24) : 0;
      return diffInDays <= 7 && diffInDays >= 0;
    });
    const data = [
      { id: 'ACTIVE', label: 'ACTIVE', count: activeDQFiles.length, value: Number(activeDQFiles.length) },
      { id: 'EXPIRED', label: 'EXPIRED', count: expiredDQFiles.length, value: Number(expiredDQFiles.length) },
      { id: 'EXPIRING', label: 'EXPIRING', count: expiringWithin1Week.length, value: Number(expiringWithin1Week.length) },
    ];
    setDQStatusPieChart(data);
  };
  
  const columns = useMemo(() => [
    { field: 'id', headerName: 'Id', renderCell: (params) => (<Link component={'button'} onClick={() => setSelectedDriver({ ...params.row, ...params.row.address })}>{params.row?.id}</Link>) },
    {
      field: 'fullName',
      headerName: 'Name',
      flex: 1,
    },
    { field: 'type', headerName: 'Type', flex: 1 },
    { field: 'phone', headerName: 'Phone', flex: 1 },
    { field: 'baseRate', headerName: 'Rate', flex: 1 },
    { field: 'licenseNumber', headerName: 'License', flex: 1 },
    { field: 'licenseExpiryDate', headerName: 'LicenseExpiryDate', flex: 1 },
    { field: 'insuranceStatus', headerName: 'Insurance', flex: 1 },
    {
      field: 'city',
      headerName: 'City',
      flex: 1,
      valueGetter: (value, row) => row.address.city,
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 1,
      valueGetter: (value, row) => row.address.state,
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 1,
      valueGetter: (value, row) => row.address.country,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => editRow(params.row)}>
            <FTIcons.EditIcon
              width={20}
              height={20}
              fill={colors.primary.main}
            />
          </IconButton>
        </Box>
      )
    },
  ], [closeDialog, editRow]);

  // filtered data
  const selectedTab = TABS[tabIndex];
  const flag = selectedTab === 'AVAILABLE';
  const tableData = isSearchEnabled ? searched : drivers;
  let filteredData = [];
  const statusCounts = [];

  if (tableData) {
    filteredData = tableData ? tableData.filter(d => d.available === flag) : [];
    statusCounts.push(
      {
        'status': 'AVAILABLE',
        'count': tableData.filter(d => d.available === true).length,
      },
      {
        'status': 'UNAVAILABLE',
        'count': tableData.filter(d => d.available === false).length,
      }
    );
  }

  return (
    <Page title="Drivers">
      <Box display={'flex'} justifyContent={'space-between'}>
        <PageHeader title='CARRIER' subTitle='Manage Your drivers & owner operators' />
        <Searchbar
          searchType='SIMPLE'
          searchKey='name'
          searchParameters={searchParameters}
          fnOnSearchHandler={executeSearch}
        />
        <Notificationbar />
      </Box>
      <Box m='20px 0 0 0' height='75vh'>
        <Box style={{ margin: '10px 0' }}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Button variant='contained' color='primary' onClick={toggleFormDialog}>New Driver</Button>
            {isProcessing && <CircularProgress style={{ margin: 'auto' }} />}
            {toast && <ToastHandler toast={toast} type={toast.type} />}
            <ButtonGroup variant='outlined' color='secondary'>
              <Button onClick={() => setIsFileImportDialogOpen(true)}>Import</Button>
              <Button onClick={downloadTemplate}>Template</Button>
              <Button onClick={downloadDrivers}>Export</Button>
            </ButtonGroup>
          </Box>
        </Box>
        <Box display={'flex'}>
          <Box flex={1}>
            <CTTabs labels={TABS} counts={statusCounts} tabIndex={tabIndex} fnOnTabChangeHandler={changeTab} />
          </Box>
          {isSearchEnabled && (
            <Box flex={1}>
              <Typography
                variant='h5'
                style={{
                  letterSpacing: 1,
                  color: '#667085',
                  textTransform: 'uppercase',
                  marginBottom: '2px'
                }}
              >
                {`${searched.length} records found`}
              </Typography>
              {createSearchChip()}
            </Box>
          )}
        </Box>
        <DataGrid rows={filteredData} columns={columns} rowHeight={40} disableSelectionOnClick />
      </Box>
      <ContentDrawer
        open={isViewDriverDrawerOpen}
        title='View Driver'
        isProcessing={isProcessing}
        toast={toast}
        handleClose={() => { setSelectedDetailTabIndex(0); setIsViewDriverDrawerOpen(false) }}
      >
        <Box mt={1}>
          <Typography variant='h6' fontWeight={600}>{selectedDriver && selectedDriver.fullName}</Typography>
          <Typography variant='body2'>{selectedDriver && selectedDriver.phone}</Typography>
          <CTTabs
            labels={DRIVER_DETAILS_TABS}
            tabIndex={detailTabIndex}
            fnOnTabChangeHandler={(tab, evt) => setSelectedDetailTabIndex(tab)}
          />
          {detailTabIndex === 0 && <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Box
                sx={{
                  height: 'auto',
                  border: `1px solid ${colors.neutral[200]}`,
                  borderRadius: '6px',
                  py: 1,
                  px: 2,
                  gap: 1.5,
                }}
              >
                <Grid container spacing={1}>
                  <Grid size={{ xs: 12 }}>
                    <FormElements.SectionTitle title='Personal Information' />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3 }}>
                    <LabelValueBox
                      label='Driver Type'
                      value={selectedDriver?.driverType}
                      labelVariant='body2'
                      valueVariant='body1'
                      valueFontWeight={500}
                    />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3 }}>
                    <LabelValueBox label='Name' value={selectedDriver?.fullName}
                      labelVariant='body2'
                      valueVariant='body1'
                      valueFontWeight={500}
                    />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3 }}>
                    <LabelValueBox label='Phone' value={selectedDriver?.phone} labelVariant='body2'
                      valueVariant='body1'
                      valueFontWeight={500}
                    />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3 }}>
                    <LabelValueBox label='Base Rate' value={selectedDriver?.baseRate} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='Email' value={selectedDriver?.email} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='Country' value={selectedDriver?.country} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='City' value={selectedDriver?.city} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='State' value={selectedDriver?.state} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}></Grid>
                  <Grid size={{ xs: 4, sm: 4 }}></Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormElements.SectionTitle title='License Information' />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='License Number' value={selectedDriver?.licenseNumber} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='Expiry Date' value={selectedDriver?.licenseExpiryDate} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='License State' value={selectedDriver?.licenseState} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormElements.SectionTitle title='Insurance Information' />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='Insurance Provider' value={selectedDriver?.insuranceCarrier} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='Insured Name' value={selectedDriver?.insuredName} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='Policy Number' value={selectedDriver?.policyNumber} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='Policy Expiry Date' value={selectedDriver?.policyExpiryDate} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 4 }}>
                    <LabelValueBox label='Certificate Holder' value={selectedDriver?.certificateHolder} />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Grid container spacing={1}>
                <Grid size={{xs: 12}}>
                  <ChartContainer
                    title='Compliance'
                    height='260px'
                    width='auto'
                  >
                    <Box sx={{ width: 'auto', height: 145 }}>
                      <PieChart
                        data={dqStatusPieChartData || []}
                        customColor={true}
                      />
                    </Box>
                    {dqStatusPieChartData && dqStatusPieChartData.map(item => (
                      <Grid container spacing={1} key={item.label}>
                        <Grid size={{ xs: 8 }}>
                          <Typography variant='body1' sx={{ marginLeft: 2 }}>
                            <span style={{
                              border: `3px solid ${CHART_COLORS[item.label]}`,
                              height: '12px',
                              display: 'inline-block',
                              margin: '0px 5px',
                            }}></span>
                            {item.label}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4}}>
                          <Typography variant='body1'>{item.count}</Typography>
                        </Grid>
                      </Grid>
                    ))}                    
                  </ChartContainer>
                  {/* <Box
                    sx={{
                      height: '100px',
                      border: `1px solid ${colors.neutral[200]}`,
                      borderRadius: '8px',
                      p: 2,
                      gap: 1.5,
                      background: getDQStatusColor(activeDQFiles.length),
                    }}
                  >
                    {isAPIProcessing && <CircularProgress sx={{margin: 'auto'}} />}                
                    <Typography variant='h4' color={colors.base.white} fontWeight={600} textAlign={'center'}>{`${activeDQFiles.length} / 10`}</Typography>
                    <Typography variant='body1' color={colors.base.white} textAlign={'center'}>{'COMPLIANCE RATING'}</Typography>
                  </Box> */}
                </Grid>
                <Grid size={{xs: 12}}>
                  <ChartContainer
                    title='Last 5 Payments'
                    height='275px'
                    width='auto'
                  >
                    <Box height={230}>
                      <BarChart
                        data={lastFivePayments || []}
                        keys={['Amount']}
                        indexKey={'PaymentDate'}
                        groupMode={'stacked'}
                        layout={'vertical'}
                        margin={{ top: 20, right: 20, bottom: 40, left: 40 }}
                        valueFormat={true}
                      />
                    </Box>                    
                  </ChartContainer>
                </Grid>
              </Grid>
            </Grid>
          </Grid>}
          {detailTabIndex === 1 && <Box mt={2}>
            <Button variant='contained' color='primary' onClick={()=>setShowDocumentForm(true)}>Add New Document</Button>          
            <DQFileManager
              showDocumentForm={showDocumentForm}
              personnelId={selectedDriver?.id}
              personnelType={'DRIVER'}
              documentList={dqFiles}
              onCloseHandler={() => setShowDocumentForm(false)}
            />            
          </Box>}
        </Box>
      </ContentDrawer>
      <FormDrawer
        open={isDriverFormDrawerOpen}
        title={`${action === 'Save' ? 'Create Driver' : 'Edit Driver'}`}
        toast={toast}
        isProcessing={isProcessing}
        handleClose={closeDialog}
        footer={
          <FormElements.FormFooter>
            <ButtonCancel action={closeDialog} />
            <ButtonSave title={action} action={() => submitForm()} />
          </FormElements.FormFooter>
        }
      >
        <DriverForm
          action={action}
          driver={driver}
          saveFormHandler={saveFormHandler}
          updateFormHandler={updateFormHandler}
          exposeSubmit={(submitFn) => setSubmitForm(() => submitFn)}
        />
      </FormDrawer>
    </Page>
  );
};

function mapStatesToProps(state) {
  const { drivers, searched, isProcessing, toast } = state.driverReducer;
  return { drivers, searched, isProcessing, toast };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(DriverAction, dispatch);
}

export default connect(mapStatesToProps, mapDispatchToProps)(DriverPage);